import { useEffect, useMemo, useState } from 'react'
import { useSearchParams } from 'react-router-dom'

import { UnmappedErrorsQuery } from '@engine/models/admin'

import { AdminFilters } from '#components/adminFilters/AdminFiltersPanel.js'
import { FilterInput } from '#components/adminFilters/FilterInput.js'
import { FilterRow } from '#components/adminFilters/FilterRow.js'
import { FilterSelect } from '#components/adminFilters/FilterSelect.js'
import { ScacTypeahead } from '#components/ScacTypeahead.js'
import {
  getRangeOptions,
  RANGE_MINUTES,
  type RangeKey,
} from '#utils/timeRanges'

type CategoryFilter = '' | 'ebol' | 'tender'

const CATEGORY_OPTIONS = [
  { value: '', label: 'All' },
  { value: 'ebol', label: 'EBOL' },
  { value: 'tender', label: 'Tender' },
]

interface LocalFiltersState {
  scac: string
  category: CategoryFilter
  range: RangeKey
  customFrom: string
  customTo: string
}

export const useUnmappedErrorsFilters = () => {
  const [searchParams, setSearchParams] = useSearchParams()

  const [localFilters, setLocalFilters] = useState<LocalFiltersState>({
    scac: searchParams.get('scac') || '',
    category: (searchParams.get('category') as CategoryFilter) || '',
    range: (searchParams.get('range') as RangeKey) || 'all',
    customFrom: searchParams.get('customFrom') || '',
    customTo: searchParams.get('customTo') || '',
  })

  // Convert local datetime strings to UTC ISO format for API
  const filters = useMemo<UnmappedErrorsQuery>(() => {
    const now = new Date()
    let startDate: string | undefined
    let endDate: string | undefined

    if (localFilters.range === 'all') {
      // No date filters for 'all'
      startDate = undefined
      endDate = undefined
    } else if (localFilters.range === 'custom') {
      startDate = localFilters.customFrom
        ? new Date(localFilters.customFrom).toISOString()
        : undefined
      endDate = localFilters.customTo
        ? new Date(localFilters.customTo).toISOString()
        : undefined
    } else {
      const minutes = RANGE_MINUTES[localFilters.range]
      const fromDate = new Date(now.getTime() - minutes * 60 * 1_000)
      startDate = fromDate.toISOString()
      endDate = now.toISOString()
    }

    return {
      scac: localFilters.scac || undefined,
      category: localFilters.category || undefined,
      startDate,
      endDate,
    }
  }, [localFilters])

  // Sync filters to URL query params
  useEffect(() => {
    const params = new URLSearchParams()
    if (localFilters.scac) params.set('scac', localFilters.scac)
    if (localFilters.category) params.set('category', localFilters.category)
    if (localFilters.range) params.set('range', localFilters.range)
    if (localFilters.customFrom)
      params.set('customFrom', localFilters.customFrom)
    if (localFilters.customTo) params.set('customTo', localFilters.customTo)
    setSearchParams(params, { replace: true })
  }, [localFilters, setSearchParams])

  return { filters, localFilters, setLocalFilters }
}

interface UnmappedErrorsFiltersProps {
  isLoading: boolean
  onRefresh: () => void
  localFilters: LocalFiltersState
  setLocalFilters: (filters: LocalFiltersState) => void
}

export const UnmappedErrorsFilters = ({
  isLoading,
  onRefresh,
  localFilters,
  setLocalFilters,
}: UnmappedErrorsFiltersProps) => {
  return (
    <AdminFilters isLoading={isLoading} onRefresh={onRefresh}>
      <FilterRow>
        <ScacTypeahead
          value={localFilters.scac}
          onChange={value => setLocalFilters({ ...localFilters, scac: value })}
        />
        <FilterSelect
          label="Category"
          value={localFilters.category}
          onChange={value =>
            setLocalFilters({
              ...localFilters,
              category: value as CategoryFilter,
            })
          }
          options={CATEGORY_OPTIONS}
        />
        <FilterSelect
          label="Time range"
          value={localFilters.range}
          onChange={value =>
            setLocalFilters({ ...localFilters, range: value as RangeKey })
          }
          options={getRangeOptions()}
        />
        {localFilters.range === 'custom' ? (
          <>
            <FilterInput
              label="From"
              value={localFilters.customFrom}
              onChange={value =>
                setLocalFilters({ ...localFilters, customFrom: value })
              }
              type="datetime-local"
            />
            <FilterInput
              label="To"
              value={localFilters.customTo}
              onChange={value =>
                setLocalFilters({ ...localFilters, customTo: value })
              }
              type="datetime-local"
            />
          </>
        ) : null}
      </FilterRow>
    </AdminFilters>
  )
}
